import Button from '@doordash/component-button'
import { Select } from '@doordash/component-fields'
import { Modal } from '@doordash/component-modal'
import NavBar from '@doordash/component-navbar'
import { Text, StackChildren } from '@doordash/design-language'
import { get } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import {
  ANDROID_CLOSE_DEEPLINK,
  CAR_COLORS,
  CAR_MAKES,
  IOS_CLOSE_DEEPLINK,
} from '../../../constants'
import * as events from '../../../events'
import { VehiclePatchData } from '../../../models/types'
import * as DA from '../../../typeModules/events'
import Form from '../../../utilities/Form'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { validators as sharedValidators } from '../../../utilities/validators'
import {
  arrayToOptions,
  modelOptions,
  VEHICLE_OTHER,
} from '../../../utilities/vehicle'
import { OsTypes } from '../../../webview/types'
import Van from '../../atoms/images/van'
import { SkipContainer, Bottom, VehicleForm, Wrapper } from './styles'

import s from './strings'

const os = detectWebviewOs()

type Props = {
  uniqueLink: string
  vehicleId: number | undefined
  getApplicationStatus?: (uniqueLink: string) => any
  updateVehicle?: (vehicleId: number, data: VehiclePatchData) => Promise<void>
  vehicleOptOut?: (uniqueLink: string) => Promise<void>
  trackNeedsVehicleInfo?: (data: DA.NeedsVehiclInfo) => Promise<void>
  trackSkipVehicleInfo?: () => Promise<void>
}

export const NeedsVehicleInfo: React.FC<Props> = ({
  uniqueLink,
  vehicleId,
  getApplicationStatus,
  updateVehicle,
  vehicleOptOut,
  trackNeedsVehicleInfo,
  trackSkipVehicleInfo,
}) => {
  const [isShowingModal, setShowingModal] = React.useState(false)
  const [isShowingVehicleForm, setShowingVehicleForm] = React.useState(false)

  React.useEffect(() => {
    const callAppStatus = async () => {
      await getApplicationStatus(uniqueLink)
    }
    callAppStatus()
    trackNeedsVehicleInfo({
      action: DA.EventActions.Rendered,
      name: DA.NeedsVehicleInfoNames.Splash,
    })
  }, [uniqueLink])

  const toggleModal = e => {
    e.stopPropagation()
    setShowingModal(!isShowingModal)
  }

  const closeWebview = () => {
    window.location.assign(
      os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
    )
  }

  const markNoCar = async () => {
    try {
      trackNeedsVehicleInfo({
        action: DA.EventActions.Submit,
        name: DA.NeedsVehicleInfoNames.Modal,
      })
      await vehicleOptOut(uniqueLink)
      trackNeedsVehicleInfo({
        action: DA.EventActions.Success,
        name: DA.NeedsVehicleInfoNames.Modal,
      })
      closeWebview()
    } catch (e) {
      trackNeedsVehicleInfo({
        action: DA.EventActions.Error,
        name: DA.NeedsVehicleInfoNames.Modal,
      })
    }
  }

  const submitForm = async data => {
    try {
      trackNeedsVehicleInfo({
        action: DA.EventActions.Submit,
        name: DA.NeedsVehicleInfoNames.Form,
      })
      await updateVehicle(vehicleId, data)
      trackNeedsVehicleInfo({
        action: DA.EventActions.Success,
        name: DA.NeedsVehicleInfoNames.Form,
      })
      window.location.assign(
        os === OsTypes.iOS ? IOS_CLOSE_DEEPLINK : ANDROID_CLOSE_DEEPLINK
      )
    } catch (e) {
      trackNeedsVehicleInfo({
        action: DA.EventActions.Error,
        name: DA.NeedsVehicleInfoNames.Form,
      })
    }
  }

  return (
    <React.Fragment>
      {isShowingModal && (
        <Modal
          title={s.modal.title()}
          secondaryAction={{
            text: s.modal.primaryButton(),
            onClick: markNoCar,
            buttonProps: {
              type: Button.Types.PrimaryPill,
            },
          }}
          primaryAction={{
            text: s.modal.secondaryButton(),
            onClick: toggleModal,
            buttonProps: {
              type: Button.Types.FlatSecondary,
            },
          }}
          hasCloseButton={false}
          isFullScreenOnMobile={false}
          headerAlignment={Modal.HeaderAlignment.Center}
          actionsFillContainer
          shouldStackButtons
        >
          <Text>{s.modal.body()}</Text>
        </Modal>
      )}
      <NavBar
        navigationType={
          isShowingVehicleForm
            ? NavBar.NavigationTypes.Back
            : NavBar.NavigationTypes.None
        }
        isSeparatorVisible={false}
        onNavigationClick={() => setShowingVehicleForm(false)}
      />
      <Wrapper isCentered={!isShowingVehicleForm}>
        {isShowingVehicleForm ? (
          <React.Fragment>
            <StackChildren size={StackChildren.Sizes.XxSmall}>
              <Text styles={Text.Styles.Bold24}>{s.form.title()}</Text>
              <Text
                styles={Text.Styles.Medium16}
                color={Text.Colors.SystemGrey60}
              >
                {s.form.subtitle()}
              </Text>
              <Form
                fields={{
                  make: '',
                  model: '',
                  color: '',
                }}
                validators={sharedValidators.vehicle}
                onSubmit={submitForm}
              >
                {(fields, handleChange, handleSubmit, { isLoading }) => (
                  <React.Fragment>
                    <VehicleForm onSubmit={handleSubmit}>
                      <StackChildren>
                        <Select
                          accessibilityDescribedBy={'make'}
                          label={s.form.makeLabel()}
                          description={s.form.required()}
                          onChange={selectedMake => {
                            handleChange('make', selectedMake)
                            handleChange(
                              'model',
                              selectedMake === VEHICLE_OTHER
                                ? VEHICLE_OTHER
                                : ''
                            )
                          }}
                          options={arrayToOptions([
                            '',
                            ...CAR_MAKES,
                            VEHICLE_OTHER,
                          ])}
                          {...fields.make}
                        />
                        <Select
                          accessibilityDescribedBy={'model'}
                          label={s.form.modelLabel()}
                          description={s.form.required()}
                          disabled={fields.make.value !== ''}
                          onChange={selectedModel =>
                            handleChange('model', selectedModel)
                          }
                          options={modelOptions(fields.make.value)}
                          {...fields.model}
                        />
                        <Select
                          accessibilityDescribedBy={'color'}
                          label={s.form.colorLabel()}
                          description={s.form.required()}
                          disabled={fields.model.value !== ''}
                          onChange={selectedColor =>
                            handleChange('color', selectedColor)
                          }
                          options={[
                            '',
                            ...arrayToOptions(CAR_COLORS, s.colors),
                          ]}
                          {...fields.color}
                        />
                      </StackChildren>
                    </VehicleForm>
                    <Bottom>
                      <Button
                        type={Button.Types.PrimaryPill}
                        size={Button.Sizes.Large}
                        onClick={handleSubmit}
                        disabled={!vehicleId}
                        state={
                          isLoading
                            ? Button.States.Loading
                            : Button.States.Default
                        }
                      >
                        {s.form.saveButton()}
                      </Button>
                    </Bottom>
                  </React.Fragment>
                )}
              </Form>
            </StackChildren>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <SkipContainer>
              <Button
                isInline
                type={Button.Types.FlatSecondary}
                size={Button.Sizes.Large}
                onClick={() => {
                  trackSkipVehicleInfo()
                  closeWebview()
                }}
              >
                {s.splash.skipButton()}
              </Button>
            </SkipContainer>
            <Text styles={Text.Styles.Bold24}>{s.splash.title()}</Text>
            <Van />
            <StackChildren>
              <Text
                styles={Text.Styles.Medium16}
                color={Text.Colors.SystemGrey60}
              >
                {s.splash.description1()}
              </Text>
              <Text
                styles={Text.Styles.Medium16}
                color={Text.Colors.SystemGrey60}
              >
                {s.splash.description2()}
              </Text>
            </StackChildren>
            <Bottom>
              <StackChildren>
                <Button
                  type={Button.Types.PrimaryPill}
                  size={Button.Sizes.Large}
                  onClick={() => {
                    setShowingVehicleForm(true)
                    trackNeedsVehicleInfo({
                      action: DA.EventActions.Rendered,
                      name: DA.NeedsVehicleInfoNames.Form,
                    })
                  }}
                >
                  {s.splash.primaryButton()}
                </Button>
                <Button
                  type={Button.Types.FlatSecondary}
                  size={Button.Sizes.Large}
                  onClick={() => {
                    setShowingModal(true)
                    trackNeedsVehicleInfo({
                      action: DA.EventActions.Rendered,
                      name: DA.NeedsVehicleInfoNames.Modal,
                    })
                  }}
                >
                  {s.splash.secondaryButton()}
                </Button>
              </StackChildren>
            </Bottom>
          </React.Fragment>
        )}
      </Wrapper>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  vehicleId: get(state, 'applicationStatus.data.vehicleId'),
})

const mapDispatchToProps = {
  getApplicationStatus: actions.getApplicationStatus,
  updateVehicle: actions.updateVehicle,
  vehicleOptOut: actions.vehicleOptOut,
  trackNeedsVehicleInfo: events.trackNeedsVehicleInfo,
  trackSkipVehicleInfo: events.trackSkipVehicleInfo,
}

export default connect(mapStateToProps, mapDispatchToProps)(NeedsVehicleInfo)
